import {
  ConductArrangeBy,
  GetConductEntriesParams,
  GetConductEntriesQueryFilters,
  GetConductEntriesQuerySort,
  SORT_DIRECTION,
  useGetConductEntriesAggregatedQuery,
  useGetConductEntriesQuery,
} from '@schooly/api';
import { useCallback, useMemo } from 'react';

import { ConductFlatQueryResult } from './ConductFlatGrid';
import { ConductGroupedQueryResult } from './ConductGroupedGrid';

type UseConductDataParams = {
  initialParams: GetConductEntriesParams;
  isGrouped: boolean;
};

export const useConductData = ({ initialParams, isGrouped }: UseConductDataParams) => {
  const flatQuery = useGetConductEntriesQuery(initialParams, {
    refetchOnMount: 'always',
    enabled: !isGrouped,
  });

  const { params, setParams } = flatQuery;

  const aggregatedSort: GetConductEntriesQuerySort = useMemo(
    () => ({
      columnTextId: 'student',
      direction:
        params.sort?.columnTextId === 'student' ? params.sort.direction : SORT_DIRECTION.ASC,
    }),
    [params.sort?.columnTextId, params.sort?.direction],
  );

  const aggregatedQuery = useGetConductEntriesAggregatedQuery(
    {
      schoolId: params.schoolId,
      filters: params.filters,
      sort: [aggregatedSort],
      query: params.query,
      arrangeBy: ConductArrangeBy.Student,
    },
    { refetchOnMount: 'always', enabled: isGrouped },
  );

  const conductLogsCount = isGrouped
    ? aggregatedQuery.data?.rows.reduce(
        (sum, row) => sum + row.type_counts.reduce((s, tc) => s + tc.count, 0),
        0,
      )
    : flatQuery.data?.pages[0]?.count;

  const flatQueryResult: ConductFlatQueryResult = { ...flatQuery, params };
  const groupedQueryResult: ConductGroupedQueryResult = aggregatedQuery;

  const setFilters = useCallback(
    (filters: GetConductEntriesQueryFilters) => {
      setParams((prev) => ({ ...prev, filters }));
    },
    [setParams],
  );

  const setSort = useCallback(
    (sort: GetConductEntriesQuerySort) => {
      setParams((prev) => ({ ...prev, sort }));
    },
    [setParams],
  );

  const setQuery = useCallback(
    (query: string) => {
      setParams((prev) => ({ ...prev, query }));
    },
    [setParams],
  );

  const resetSortForGrouped = useCallback(() => {
    setParams((prev) => ({
      ...prev,
      sort: { columnTextId: 'student', direction: SORT_DIRECTION.ASC },
    }));
  }, [setParams]);

  const error = isGrouped ? aggregatedQuery.error : flatQuery.error;

  return {
    params,
    flatQueryResult,
    groupedQueryResult,
    conductLogsCount,
    error,
    setFilters,
    setSort,
    setQuery,
    resetSortForGrouped,
  };
};
